import { UserStatus } from "../types/create-user.types";

/**
 * Would applying this status to YOURSELF end your own access?
 *
 * `JwtPermissionsGuard` re-reads the principal from the database on every request and answers 401 for
 * `SUSPENDED` and for `INACTIVE`. So either one takes effect on the very next call — including the `PATCH
 * /internal/users/:id` that would undo it. Login refuses both statuses too, and the only self-service
 * mutation of your own row (`PATCH /auth/profile`) cannot write `status`, so there is no way back from the
 * client: recovery needs another operator holding `users:update`, or the database.
 *
 * **The API will not stop this.** `UsersService.update` authorizes on the route permission and a tenancy
 * check only — it never compares the target id against the caller's, and `status` is not among the fields
 * stripped for organization members. Exactly like `remove`, which the module already guards for the same
 * reason. The client is the whole guard.
 *
 * Two statuses are deliberately NOT lockouts, and neither is an oversight:
 *
 * - `ACTIVE` is the direction that RESTORES access, so it stays available on your own row.
 * - `PENDING_VERIFICATION` is not rejected by that guard at all (only login-adjacent flows like 2FA enrolment
 *   require `ACTIVE`), and no surface can send it anyway.
 *
 * A predicate rather than three inline comparisons because it is the rule those menu items share, and the gap
 * it closes was precisely the rule being applied to one of them: `isCurrentUser` hid Delete while Suspend and
 * Deactivate — equally unrecoverable from this UI — stayed clickable on your own row.
 *
 * One asymmetry worth knowing when reading the copy: unlike delete, a status change does **not** revoke the
 * session rows. The operator is refused rather than signed out, and their refresh token keeps rotating, which
 * makes a self-suspension look survivable while every guarded endpoint is already closed.
 */
export function isSelfLockoutStatus(status: UserStatus): boolean {
  return status === UserStatus.SUSPENDED || status === UserStatus.INACTIVE;
}
